// src/pages/Home.jsx — Epic Games Store style, fully mobile-optimized
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import GameCard from "../components/common/GameCard";
import { getGames } from "../services/gameService";
import { useLang } from "../context/LanguagesContext";
import logoImg from "../assets/phsar_game.PNG";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
.pg { background:#121212; color:#fff; font-family:'Inter',sans-serif; overflow-x:hidden; min-height:100vh; }

/* ── HERO ── */
.hero {
  position:relative; min-height:100vh;
  display:flex; align-items:center;
  background:#121212; overflow:hidden;
  padding:80px 0 0;
}
.hero-bg-grid {
  position:absolute; inset:0; z-index:0;
  background-image:
    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size:48px 48px;
}
.hero-bg-glow {
  position:absolute; z-index:1;
  top:-20%; left:50%; transform:translateX(-50%);
  width:900px; height:600px;
  background:radial-gradient(ellipse, rgba(0,120,242,0.12) 0%, transparent 70%);
  pointer-events:none;
}
.hero-inner {
  position:relative; z-index:2;
  max-width:1400px; margin:0 auto; width:100%;
  padding:0 80px;
  display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:center;
}
.hero-eyebrow {
  display:inline-flex; align-items:center; gap:8px;
  background:rgba(0,120,242,0.1); border:1px solid rgba(0,120,242,0.3);
  border-radius:4px; padding:6px 14px; margin-bottom:28px;
  font-size:12px; font-weight:600; color:#0078F2; letter-spacing:1px; text-transform:uppercase;
}
.hero-dot { width:6px; height:6px; background:#0078F2; border-radius:50%; animation:pulse 2s infinite; }
@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.7)} }

.hero-title {
  font-size:clamp(40px,5.5vw,72px); font-weight:900; line-height:1.05;
  letter-spacing:-2px; color:#fff; margin-bottom:20px;
}
.hero-title .blue { color:#0078F2; }
.hero-desc {
  font-size:17px; color:#a3a3a3; line-height:1.7; margin-bottom:40px;
  max-width:480px; font-weight:400;
}
.hero-actions { display:flex; gap:12px; flex-wrap:wrap; margin-bottom:40px; }
.hero-btn-primary {
  font-family:'Inter',sans-serif; font-size:15px; font-weight:700;
  color:#fff; background:#0078F2; border:none; border-radius:6px;
  padding:14px 32px; cursor:pointer; text-decoration:none; transition:all 0.15s;
}
.hero-btn-primary:hover { background:#0a84ff; transform:translateY(-1px); }
.hero-btn-secondary {
  font-family:'Inter',sans-serif; font-size:15px; font-weight:600;
  color:#a3a3a3; background:rgba(255,255,255,0.06);
  border:1px solid rgba(255,255,255,0.12); border-radius:6px;
  padding:14px 28px; cursor:pointer; text-decoration:none; transition:all 0.15s;
}
.hero-btn-secondary:hover { color:#fff; background:rgba(255,255,255,0.1); }

/* ── SEARCH BAR ── */
.hero-search-wrap { position:relative; margin-bottom:32px; }
.hero-search {
  display:flex; align-items:center; gap:0;
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.1);
  border-radius:10px; overflow:hidden;
  transition:border-color 0.15s;
}
.hero-search:focus-within { border-color:rgba(0,120,242,0.5); }
.hero-search-icon {
  padding:0 14px; color:#555; display:flex; align-items:center; flex-shrink:0;
}
.hero-search-input {
  flex:1; background:none; border:none; outline:none;
  font-family:'Inter',sans-serif; font-size:15px; font-weight:500;
  color:#fff; padding:14px 0;
  caret-color:#0078F2;
}
.hero-search-input::placeholder { color:#444; }
.hero-search-btn {
  font-family:'Inter',sans-serif; font-size:13px; font-weight:700;
  color:#fff; background:#0078F2; border:none;
  padding:0 20px; height:48px; cursor:pointer; transition:background 0.15s;
  flex-shrink:0;
}
.hero-search-btn:hover { background:#0a84ff; }

/* Search dropdown */
.search-dropdown {
  position:absolute; top:calc(100% + 4px); left:0; right:0; z-index:200;
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.1); border-radius:10px;
  box-shadow:0 16px 40px rgba(0,0,0,0.6); overflow:hidden;
  max-height:320px; overflow-y:auto;
}
.search-drop-item {
  display:flex; align-items:center; gap:12px;
  padding:10px 14px; text-decoration:none; color:#fff; transition:background 0.1s;
}
.search-drop-item:hover { background:rgba(255,255,255,0.05); }
.search-drop-img {
  width:44px; height:44px; border-radius:6px; object-fit:cover;
  background:#2a2a2a; flex-shrink:0;
  display:flex; align-items:center; justify-content:center; font-size:20px;
}
.search-drop-title { font-size:14px; font-weight:600; color:#fff; margin-bottom:2px; }
.search-drop-price { font-size:13px; font-weight:700; color:#0078F2; }
.search-drop-empty { padding:24px; text-align:center; color:#555; font-size:13px; }

.hero-stats { display:flex; gap:0; border:1px solid rgba(255,255,255,0.08); border-radius:8px; overflow:hidden; width:fit-content; }
.stat { padding:16px 28px; text-align:center; border-right:1px solid rgba(255,255,255,0.08); }
.stat:last-child { border-right:none; }
.stat-n { font-size:26px; font-weight:800; color:#fff; line-height:1; margin-bottom:4px; }
.stat-n span { color:#0078F2; }
.stat-l { font-size:11px; font-weight:500; color:#6b6b6b; text-transform:uppercase; letter-spacing:0.8px; }

/* Hero right */
.hero-right { position:relative; }
.hero-showcase {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08); border-radius:12px;
  overflow:hidden; position:relative;
}
.hero-showcase-img { width:100%; aspect-ratio:16/10; object-fit:cover; display:block; }
.hero-showcase-placeholder {
  width:100%; aspect-ratio:16/10; background:linear-gradient(135deg,#1a1a1a,#2a2a2a);
  display:flex; align-items:center; justify-content:center; font-size:80px;
}
.hero-showcase-info { padding:20px; background:linear-gradient(to top,#1a1a1a,rgba(26,26,26,0.8)); }
.hero-showcase-label { font-size:11px; font-weight:600; color:#0078F2; text-transform:uppercase; letter-spacing:1px; margin-bottom:6px; }
.hero-showcase-title { font-size:22px; font-weight:800; color:#fff; margin-bottom:8px; }
.hero-showcase-price { font-size:24px; font-weight:800; color:#0078F2; }
.hero-showcase-btn {
  position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
  background:#0078F2; border:none; border-radius:50%;
  width:60px; height:60px; font-size:24px; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  opacity:0; transition:all 0.2s; box-shadow:0 4px 24px rgba(0,120,242,0.4);
}
.hero-showcase:hover .hero-showcase-btn { opacity:1; }

/* Platform pills */
.platform-row { display:flex; gap:8px; flex-wrap:wrap; margin-top:20px; }
.platform-pill {
  display:inline-flex; align-items:center; gap:6px;
  background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08);
  border-radius:4px; padding:6px 12px;
  font-size:12px; font-weight:500; color:#6b6b6b;
}

/* ── SECTION BASE ── */
.section { max-width:1400px; margin:0 auto; padding:80px; }
.sect-header { margin-bottom:40px; display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; }
.sect-tag { font-size:12px; font-weight:600; color:#0078F2; text-transform:uppercase; letter-spacing:1px; margin-bottom:8px; }
.sect-title { font-size:clamp(24px,3vw,36px); font-weight:800; color:#fff; letter-spacing:-0.5px; }
.sect-view-all {
  font-size:14px; font-weight:600; color:#0078F2; text-decoration:none; white-space:nowrap;
  display:flex; align-items:center; gap:4px; transition:all 0.15s;
}
.sect-view-all:hover { color:#0a84ff; }

/* ── FEATURES ── */
.features-bg { background:#1a1a1a; border-top:1px solid rgba(255,255,255,0.06); border-bottom:1px solid rgba(255,255,255,0.06); }
.features-inner { max-width:1400px; margin:0 auto; padding:48px 80px; }
.feats-grid {
  display:grid; grid-template-columns:repeat(4,1fr);
  gap:1px; background:rgba(255,255,255,0.06);
  border:1px solid rgba(255,255,255,0.06); border-radius:8px; overflow:hidden;
}
.feat { background:#1a1a1a; padding:28px 22px; transition:background 0.15s; }
.feat:hover { background:#202020; }
.feat-icon-wrap {
  width:40px; height:40px; border-radius:8px; margin-bottom:14px;
  display:flex; align-items:center; justify-content:center;
}
.feat-title { font-size:15px; font-weight:700; color:#fff; margin-bottom:6px; }
.feat-desc { font-size:13px; color:#6b6b6b; line-height:1.6; }

/* ── GAMES GRID ── */
.games-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:16px; }
.skel { background:#1a1a1a; border-radius:8px; overflow:hidden; animation:shim 1.5s ease infinite; }
@keyframes shim { 0%,100%{opacity:.5} 50%{opacity:.8} }
.skel-img { aspect-ratio:3/4; background:#2a2a2a; }
.skel-body { padding:14px; display:flex; flex-direction:column; gap:8px; }
.skel-line { height:10px; border-radius:4px; background:#2a2a2a; }

/* ── HOW IT WORKS ── */
.steps-section { background:#1a1a1a; border-top:1px solid rgba(255,255,255,0.06); border-bottom:1px solid rgba(255,255,255,0.06); }
.steps-inner { max-width:1400px; margin:0 auto; padding:56px 80px; }
.steps-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:0; }
.step { padding:40px 32px; position:relative; border-right:1px solid rgba(255,255,255,0.06); }
.step:last-child { border-right:none; }
.step-num { font-size:48px; font-weight:900; color:rgba(0,120,242,0.12); line-height:1; margin-bottom:16px; letter-spacing:-2px; }
.step-icon { font-size:28px; margin-bottom:14px; display:block; }
.step-title { font-size:17px; font-weight:700; color:#fff; margin-bottom:8px; }
.step-desc { font-size:14px; color:#6b6b6b; line-height:1.65; }

/* ── CTA ── */
.cta-wrap { max-width:1400px; margin:0 auto; padding:80px; }
.cta {
  background:linear-gradient(135deg,#0078F2 0%,#0055b3 100%);
  border-radius:12px; padding:64px 80px;
  display:flex; align-items:center; justify-content:space-between; gap:40px;
  position:relative; overflow:hidden;
}
.cta::before { content:''; position:absolute; right:-80px; top:-80px; width:300px; height:300px; background:rgba(255,255,255,0.05); border-radius:50%; }
.cta-title { font-size:clamp(24px,3vw,40px); font-weight:900; color:#fff; letter-spacing:-1px; margin-bottom:10px; }
.cta-desc { font-size:16px; color:rgba(255,255,255,0.75); line-height:1.6; max-width:480px; }
.cta-btn {
  font-family:'Inter',sans-serif; font-size:15px; font-weight:700; color:#0078F2;
  background:#fff; border:none; border-radius:6px; padding:16px 36px;
  cursor:pointer; text-decoration:none; white-space:nowrap; transition:all 0.15s; flex-shrink:0;
}
.cta-btn:hover { background:#f0f0f0; transform:translateY(-1px); }

/* ── FOOTER ── */
.footer { background:#0d0d0d; border-top:1px solid rgba(255,255,255,0.06); padding:56px 80px 32px; }
.footer-inner { max-width:1400px; margin:0 auto; }
.footer-top {
  display:grid; grid-template-columns:1.8fr 1fr 1fr 1fr; gap:48px;
  padding-bottom:40px; border-bottom:1px solid rgba(255,255,255,0.06); margin-bottom:28px;
}
.footer-logo {
  display:inline-flex; align-items:center; gap:6px;
  font-size:18px; font-weight:800; color:#fff; text-decoration:none; margin-bottom:14px;
}
.footer-logo-img { width:28px; height:28px; object-fit:contain; }
.footer-logo-accent { color:#0078F2; }
.footer-tagline { font-size:13px; color:#6b6b6b; line-height:1.65; max-width:240px; margin-bottom:20px; }
.footer-socials { display:flex; gap:8px; flex-wrap:wrap; }
.footer-social-btn {
  display:inline-flex; align-items:center; gap:7px;
  background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08);
  border-radius:6px; padding:8px 14px;
  font-size:13px; font-weight:500; color:#a3a3a3; text-decoration:none; transition:all 0.15s;
}
.footer-social-btn:hover { background:rgba(0,120,242,0.12); border-color:rgba(0,120,242,0.3); color:#fff; transform:translateY(-1px); }
.footer-col-title { font-size:11px; font-weight:700; color:#fff; text-transform:uppercase; letter-spacing:1.2px; margin-bottom:18px; }
.footer-col-links { display:flex; flex-direction:column; gap:12px; }
.footer-link { font-size:13px; font-weight:500; color:#6b6b6b; text-decoration:none; transition:color 0.15s; }
.footer-link:hover { color:#fff; }
.footer-bottom { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; }
.footer-copy { font-size:12px; color:#3a3a3a; }
.footer-bottom-links { display:flex; gap:20px; flex-wrap:wrap; }
.footer-bottom-link { font-size:12px; color:#3a3a3a; text-decoration:none; transition:color 0.15s; }
.footer-bottom-link:hover { color:#6b6b6b; }

/* ════════════════════════════════════════
   RESPONSIVE — TABLET (≤ 1100px)
════════════════════════════════════════ */
@media(max-width:1100px) {
  .hero-inner { grid-template-columns:1fr; padding:0 40px; gap:40px; }
  .hero-right { display:none; }
  .feats-grid { grid-template-columns:repeat(2,1fr); }
  .steps-grid { grid-template-columns:repeat(2,1fr); }
  .step { border-bottom:1px solid rgba(255,255,255,0.06); }
  .section { padding:60px 40px; }
  .features-inner { padding:48px 40px; }
  .steps-inner { padding:56px 40px; }
  .cta { padding:48px 40px; flex-direction:column; }
  .cta-wrap { padding:60px 40px; }
  .footer { padding:48px 40px 28px; }
  .footer-top { grid-template-columns:1fr 1fr; gap:32px; }
}

/* ════════════════════════════════════════
   RESPONSIVE — MOBILE (≤ 767px)
════════════════════════════════════════ */
@media(max-width:767px) {

  /* ── Hero — centered ── */
  .hero { min-height:auto; padding:72px 0 0; }
  .hero-inner { padding:32px 16px 40px; gap:24px; }
  .hero-left { display:flex; flex-direction:column; align-items:center; text-align:center; }

  .hero-eyebrow { font-size:10px; padding:5px 10px; margin-bottom:14px; }

  .hero-title {
    font-size:clamp(28px,8vw,40px); letter-spacing:-1px; margin-bottom:12px;
  }
  .hero-desc { font-size:14px; margin-bottom:20px; max-width:100%; }

  .hero-actions { justify-content:center; gap:8px; margin-bottom:20px; }
  .hero-btn-primary, .hero-btn-secondary { font-size:14px; padding:12px 22px; }

  /* Search bar — full width */
  .hero-search-wrap { width:100%; margin-bottom:20px; }
  .hero-search-input { font-size:14px; padding:12px 0; }
  .hero-search-btn { padding:0 14px; height:44px; font-size:12px; }

  /* Stats — 2×2 grid, full width */
  .hero-stats {
    display:grid; grid-template-columns:1fr 1fr;
    width:100%; border-radius:8px;
  }
  .stat { padding:12px 8px; }
  .stat-n { font-size:20px; }
  .stat-l { font-size:9px; }
  .stat:nth-child(2) { border-right:none; }
  .stat:nth-child(3) { border-top:1px solid rgba(255,255,255,0.08); }
  .stat:nth-child(4) { border-top:1px solid rgba(255,255,255,0.08); border-right:none; }

  .platform-row { display:none; }

  /* ── Features — full width, 2-col grid ── */
  .features-inner { padding:28px 16px; }
  .feats-grid {
    grid-template-columns:repeat(2,1fr);
    gap:1px;
  }
  .feat { padding:16px 14px; }
  .feat-icon-wrap { width:32px; height:32px; margin-bottom:10px; }
  .feat-title { font-size:13px; margin-bottom:4px; }
  .feat-desc { font-size:12px; line-height:1.5; }

  /* ── Sections ── */
  .section { padding:32px 16px; }

  /* ── Games grid — 2 columns ── */
  .games-grid { grid-template-columns:repeat(2,1fr); gap:10px; }

  /* ── How It Works — 1 column, full width ── */
  .steps-inner { padding:28px 16px; }
  .steps-grid { grid-template-columns:1fr; }
  .step {
    padding:20px 16px;
    border-right:none;
    border-bottom:1px solid rgba(255,255,255,0.06);
    display:flex; align-items:flex-start; gap:14px;
  }
  .step:last-child { border-bottom:none; }
  .step-left { flex-shrink:0; }
  .step-num { font-size:28px; margin-bottom:0; line-height:1; }
  .step-icon { font-size:20px; margin-bottom:0; }
  .step-content { flex:1; }
  .step-title { font-size:14px; margin-bottom:4px; }
  .step-desc { font-size:12px; line-height:1.6; }

  /* ── CTA ── */
  .cta-wrap { padding:20px 16px; }
  .cta { padding:28px 20px; border-radius:10px; flex-direction:column; gap:16px; text-align:center; }
  .cta-desc { max-width:100%; font-size:14px; }
  .cta-btn { width:100%; text-align:center; padding:14px 24px; }

  /* ── Footer ── */
  .footer { padding:32px 16px 100px; }

  /* footer-top: brand full-width on top, then 3 link cols below */
  .footer-top {
    display:grid;
    grid-template-columns:repeat(3, 1fr);
    grid-template-rows:auto auto;
    gap:20px 12px;
  }

  /* Brand block spans all 3 columns, centered */
  .footer-top > *:first-child {
    grid-column:1 / -1;
    display:flex; flex-direction:column; align-items:center; text-align:center;
    padding-bottom:20px;
    border-bottom:1px solid rgba(255,255,255,0.06);
  }
  .footer-tagline { max-width:100%; }
  .footer-socials { justify-content:center; }

  /* 3 link columns — each group centered */
  .footer-top > *:not(:first-child) {
    display:flex; flex-direction:column; align-items:center; text-align:center;
  }
  .footer-col-title { font-size:10px; margin-bottom:10px; }
  .footer-col-links { align-items:center; gap:8px; }
  .footer-link { font-size:12px; }

  .footer-bottom { flex-direction:column; align-items:center; gap:8px; text-align:center; }
  .footer-bottom-links { justify-content:center; gap:12px; flex-wrap:wrap; }

  /* Sect header — centered on mobile */
  .sect-header { flex-direction:column; align-items:center; text-align:center; }
  .sect-tag { font-size:10px; }
  .sect-title { font-size:20px; }
  .sect-view-all { font-size:12px; }
}
`;

// SVG icons
const IconFacebook = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
  </svg>
);
const IconTelegram = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const IconSearchHero = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [allGames, setAllGames] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [hero,     setHero]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [searchQ,  setSearchQ]  = useState("");
  const [showDrop, setShowDrop] = useState(false);

  useEffect(() => {
    getGames().then(games => {
      setAllGames(games);
      const feat = games.filter(g => g.featured);
      setHero(feat[0] || games[0] || null);
      setFeatured(feat.slice(0, 6));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const searchResults = searchQ.trim().length > 1
    ? allGames.filter(g =>
        g.title?.toLowerCase().includes(searchQ.toLowerCase()) ||
        g.genre?.toLowerCase().includes(searchQ.toLowerCase())
      ).slice(0, 6)
    : [];

  function handleSearchSubmit(e) {
    e.preventDefault();
    if (searchQ.trim()) navigate(`/store?q=${encodeURIComponent(searchQ.trim())}`);
  }

  const FEATURES = [
    { icon:"⚡", title:t("feat_instant"), desc:t("feat_instant_desc"), color:"#0078F2", bg:"rgba(0,120,242,0.12)" },
    { icon:"🔒", title:t("feat_offline"), desc:t("feat_offline_desc"), color:"#22c55e", bg:"rgba(34,197,94,0.1)" },
    { icon:"💰", title:t("feat_price"),   desc:t("feat_price_desc"),   color:"#eab308", bg:"rgba(234,179,8,0.1)" },
    { icon:"💬", title:t("feat_support"), desc:t("feat_support_desc"), color:"#a78bfa", bg:"rgba(167,139,250,0.1)" },
  ];

  const STEPS = [
    { n:"01", icon:"🛒", title:t("step1_title"), desc:t("step1_desc") },
    { n:"02", icon:"🔑", title:t("step2_title"), desc:t("step2_desc") },
    { n:"03", icon:"🎮", title:t("step3_title"), desc:t("step3_desc") },
    { n:"04", icon:"🏆", title:t("step4_title"), desc:t("step4_desc") },
  ];

  return (
    <>
      <style>{S}</style>
      <div className="pg">
        <Navbar />

        {/* ── HERO ── */}
        <section className="hero">
          <div className="hero-bg-grid" />
          <div className="hero-bg-glow" />
          <div className="hero-inner">
            <div className="hero-left">
              <div className="hero-eyebrow">
                <span className="hero-dot" />
                {t("hero_eyebrow")}
              </div>
              <h1 className="hero-title">
                {t("hero_title")}<br />
                <span className="blue">{t("hero_accent")}</span>
              </h1>
              <p className="hero-desc">{t("hero_sub")}</p>

              {/* Search */}
              <div
                className="hero-search-wrap"
                onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setShowDrop(false); }}
              >
                <form className="hero-search" onSubmit={handleSearchSubmit}>
                  <span className="hero-search-icon"><IconSearchHero /></span>
                  <input
                    className="hero-search-input"
                    placeholder={t("search_placeholder") || "Search games, genres…"}
                    value={searchQ}
                    onChange={e => { setSearchQ(e.target.value); setShowDrop(true); }}
                    onFocus={() => setShowDrop(true)}
                  />
                  <button type="submit" className="hero-search-btn">Search</button>
                </form>
                {showDrop && searchQ.trim().length > 1 && (
                  <div className="search-dropdown">
                    {searchResults.length === 0 ? (
                      <div className="search-drop-empty">No games found for "{searchQ}"</div>
                    ) : searchResults.map(g => (
                      <Link
                        key={g.id}
                        to={`/game/${g.id}`}
                        className="search-drop-item"
                        onClick={() => { setShowDrop(false); setSearchQ(""); }}
                        tabIndex={0}
                      >
                        {g.coverImage
                          ? <img src={g.coverImage} alt={g.title} className="search-drop-img" />
                          : <div className="search-drop-img">🎮</div>
                        }
                        <div>
                          <div className="search-drop-title">{g.title}</div>
                          <div className="search-drop-price">${g.price?.toFixed(2)}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="hero-actions">
                <Link to="/store" className="hero-btn-primary">{t("hero_browse")}</Link>
                <Link to="/how-to-use" className="hero-btn-secondary">{t("hero_how")}</Link>
              </div>

              <div className="hero-stats">
                <div className="stat"><div className="stat-n">100<span>+</span></div><div className="stat-l">{t("stat_games")}</div></div>
                <div className="stat"><div className="stat-n">500<span>+</span></div><div className="stat-l">{t("stat_customers")}</div></div>
                <div className="stat"><div className="stat-n">$2<span>.99</span></div><div className="stat-l">{t("stat_price")}</div></div>
                <div className="stat"><div className="stat-n">24<span>/7</span></div><div className="stat-l">{t("stat_support")}</div></div>
              </div>

              <div className="platform-row">
                {[["🎮","Steam"],["⬛","Epic Games"],["👤","Game Account"]].map(([icon,label]) => (
                  <span key={label} className="platform-pill">{icon} {label}</span>
                ))}
              </div>
            </div>

            {/* Hero showcase — desktop only */}
            <div className="hero-right">
              <div className="hero-showcase">
                {hero?.coverImage
                  ? <img src={hero.coverImage} alt={hero.title} className="hero-showcase-img" />
                  : <div className="hero-showcase-placeholder">🎮</div>
                }
                <Link to={hero ? `/game/${hero.id}` : "/store"} className="hero-showcase-btn">▶</Link>
                <div className="hero-showcase-info">
                  <div className="hero-showcase-label">Featured Game</div>
                  <div className="hero-showcase-title">{hero?.title || "Browse Games"}</div>
                  <div className="hero-showcase-price">{hero ? `$${hero.price?.toFixed(2)}` : ""}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FEATURES ── */}
        <div className="features-bg">
          <div className="features-inner">
            <div className="sect-header" style={{ marginBottom:24 }}>
              <div>
                <div className="sect-tag">{t("section_why")}</div>
                <div className="sect-title">{t("section_everything")}</div>
              </div>
            </div>
            <div className="feats-grid">
              {FEATURES.map(f => (
                <div key={f.title} className="feat">
                  <div className="feat-icon-wrap" style={{ background:f.bg }}>
                    <span style={{ fontSize:18 }}>{f.icon}</span>
                  </div>
                  <div className="feat-title">{f.title}</div>
                  <div className="feat-desc">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── FEATURED GAMES ── */}
        <div className="section">
          <div className="sect-header">
            <div>
              <div className="sect-tag">{t("section_featured_tag")}</div>
              <div className="sect-title">{t("section_featured")}</div>
            </div>
            <Link to="/store" className="sect-view-all">{t("view_all")} →</Link>
          </div>
          {loading ? (
            <div className="games-grid">
              {[...Array(6)].map((_,i) => (
                <div key={i} className="skel">
                  <div className="skel-img" />
                  <div className="skel-body">
                    <div className="skel-line" style={{ width:"60%" }} />
                    <div className="skel-line" />
                    <div className="skel-line" style={{ width:"40%", background:"rgba(0,120,242,0.15)" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : featured.length > 0 ? (
            <div className="games-grid">
              {featured.map((g,i) => <GameCard key={g.id} game={g} index={i} />)}
            </div>
          ) : (
            <div style={{ textAlign:"center", padding:"48px 0", color:"#6b6b6b" }}>
              <div style={{ fontSize:48, marginBottom:16 }}>🎮</div>
              <div style={{ fontSize:18, fontWeight:600, color:"#a3a3a3", marginBottom:8 }}>Games Coming Soon</div>
              <div style={{ fontSize:14 }}>Check back soon!</div>
            </div>
          )}
        </div>

        {/* ── HOW IT WORKS ── */}
        <div className="steps-section">
          <div className="steps-inner">
            <div className="sect-header" style={{ marginBottom:32 }}>
              <div>
                <div className="sect-tag">{t("how_tag")}</div>
                <div className="sect-title">{t("how_title")}</div>
              </div>
            </div>
            <div className="steps-grid">
              {STEPS.map(s => (
                <div key={s.n} className="step">
                  {/* On mobile these get wrapped in flex — step-left / step-content divs */}
                  <div className="step-left">
                    <div className="step-num">{s.n}</div>
                    <span className="step-icon">{s.icon}</span>
                  </div>
                  <div className="step-content">
                    <div className="step-title">{s.title}</div>
                    <div className="step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="cta-wrap">
          <div className="cta">
            <div className="cta-left">
              <div className="cta-title">{t("cta_title")}</div>
              <p className="cta-desc">{t("cta_desc")}</p>
            </div>
            <Link to="/store" className="cta-btn">{t("cta_btn")}</Link>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <footer className="footer">
          <div className="footer-inner">
            <div className="footer-top">
              {/* Brand */}
              <div>
                <Link to="/" className="footer-logo">
                  <img src={logoImg} alt="PhsarGame" className="footer-logo-img" />
                  Phsar<span className="footer-logo-accent">Game</span>
                </Link>
                <p className="footer-tagline">
                  Cambodia's #1 game store — instant delivery, affordable prices, 24/7 support.
                </p>
                <div className="footer-socials">
                  <a href="https://www.facebook.com/phsargame" className="footer-social-btn" target="_blank" rel="noreferrer">
                    <IconFacebook /> Facebook
                  </a>
                  <a href="https://t.me/phsargame" className="footer-social-btn" target="_blank" rel="noreferrer">
                    <IconTelegram /> Telegram
                  </a>
                </div>
              </div>

              {/* Shop */}
              <div>
                <div className="footer-col-title">Shop</div>
                <div className="footer-col-links">
                  <Link to="/store" className="footer-link">{t("nav_store")}</Link>
                  <Link to="/how-to-use" className="footer-link">{t("nav_how")}</Link>
                  <Link to="/orders" className="footer-link">My Orders</Link>
                </div>
              </div>

              {/* Account */}
              <div>
                <div className="footer-col-title">Account</div>
                <div className="footer-col-links">
                  <Link to="/login" className="footer-link">{t("nav_login")}</Link>
                  <Link to="/register" className="footer-link">{t("nav_started")}</Link>
                </div>
              </div>

              {/* Legal */}
              <div>
                <div className="footer-col-title">Legal</div>
                <div className="footer-col-links">
                  <Link to="/terms" className="footer-link">Terms &amp; Rules</Link>
                  <Link to="/privacy" className="footer-link">Privacy Policy</Link>
                </div>
              </div>
            </div>

            <div className="footer-bottom">
              <div className="footer-copy">© {new Date().getFullYear()} Phsar Game Cambodia. All rights reserved.</div>
              <div className="footer-bottom-links">
                <Link to="/terms" className="footer-bottom-link">Terms &amp; Rules</Link>
                <Link to="/privacy" className="footer-bottom-link">Privacy Policy</Link>
                <a href="https://www.facebook.com/phsargame" className="footer-bottom-link" target="_blank" rel="noreferrer">Facebook</a>
                <a href="https://t.me/phsargame" className="footer-bottom-link" target="_blank" rel="noreferrer">Telegram</a>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}